import React from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Globe2, ArrowUpRight } from "lucide-react";
import { ALMEDA_LOGO, ALMEDA_URL } from "@/data/brands";
import { useT } from "@/i18n/LanguageContext";

// "Part of the Group" — corporate-provenance section that sits between the
// Sustainability section and the PortfolioPromo. Positions Hupla as an asset
// of a Swiss investment holding — a serious signal for B2B distributors who
// research the parent company before onboarding.
// -----------------------------------------------------------------------------
export default function GroupPanel() {
  const { t } = useT();

  const kicker = t("group.kicker");
  const title = t("group.title");
  const body = t("group.body");
  const cta = t("group.cta");

  const pillars = [
    {
      key: "swiss",
      icon: MapPin,
      titleKey: "group.pillars.swiss.title",
      bodyKey: "group.pillars.swiss.body",
    },
    {
      key: "long",
      icon: Clock,
      titleKey: "group.pillars.long.title",
      bodyKey: "group.pillars.long.body",
    },
    {
      key: "global",
      icon: Globe2,
      titleKey: "group.pillars.global.title",
      bodyKey: "group.pillars.global.body",
    },
  ];

  return (
    <section
      id="group"
      className="relative py-24 lg:py-32 overflow-hidden bg-white"
      data-testid="group-panel"
    >
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* LEFT — Almeda mark + positioning */}
          <div className="lg:col-span-5">
            <p
              className="text-[0.7rem] md:text-xs font-bold uppercase mb-5"
              style={{ color: "#C41947", letterSpacing: "0.36em" }}
            >
              {kicker}
            </p>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="font-editorial italic leading-[1.05] text-[clamp(2rem,3.8vw,3.25rem)] text-[#0F1A12]"
            >
              {title}
            </motion.h2>

            <div
              className="mt-6 h-px w-24"
              style={{ background: "linear-gradient(90deg, #C41947, transparent)" }}
            />

            <p className="mt-8 text-base md:text-lg leading-[1.75] text-[#0F1A12]/85 max-w-lg">
              {body}
            </p>

            <div className="mt-10 flex items-center gap-5">
              <img
                src={ALMEDA_LOGO}
                alt="De Almeda Seligmann Capital AG"
                data-testid="group-panel-logo"
                className="h-14 w-auto"
              />
              <div>
                <p className="text-sm font-semibold text-[#0F1A12]">
                  De Almeda Seligmann Capital AG
                </p>
                <p className="text-xs text-[#0F1A12]/60">Zug, Switzerland</p>
              </div>
            </div>

            <a
              href={ALMEDA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="group-panel-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full pl-6 pr-2 py-2 text-sm font-medium transition-transform hover:scale-[1.02] shadow-sm bg-[#0F1A12] text-white"
            >
              {cta}
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#C41947]">
                <ArrowUpRight className="w-4 h-4 text-white" />
              </span>
            </a>
          </div>

          {/* RIGHT — 3 trust pillars */}
          <div className="lg:col-span-7 grid gap-4 md:gap-5">
            {pillars.map((pillar, i) => {
              const Icon = pillar.icon;
              const pt = t(pillar.titleKey);
              const pb = t(pillar.bodyKey);
              return (
                <motion.div
                  key={pillar.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="relative flex gap-5 items-start rounded-2xl border border-[#0F1A12]/10 bg-[#FAF7F2] p-6 md:p-7"
                  data-testid={`group-pillar-${pillar.key}`}
                >
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#0F1A12] text-white">
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-[#0F1A12] mb-1">
                      {pt}
                    </h3>
                    <p className="text-sm text-[#0F1A12]/70 leading-[1.65]">
                      {pb}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
